import _, { filter, find, get, head, mapValues, some, values } from 'lodash';
import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  IDiscoveryTracked,
  ILayout,
  IVizOptions,
  IViz,
  IVizQueryError,
  FieldDictionary,
} from '../../../discovery';
import { VIZ_SELECTORS, getConfigPanelDetail } from './viz-selectors';
import { ChartSpecs } from '../../../discovery/ChartSpecs';
import { IHiddenFieldWarnings, Viz } from '../../../discovery/VizUtil';
import { IAnyAttribute, IAppliedFilters } from '../../../datasets';
import {
  addDashletSuffix,
  DynamicFields,
  stripDashletSuffix,
  VIZ_OPTION_IDS,
} from '../../Constants';
import {
  convertSugarFilter,
  REF_TO,
} from '../../utilities/sugar-filter-converter/sugar-filter-converter';
import { ILinkToReport } from '../../../discovery/viz-redirect';
import { parseJSON } from '../../Util';
import { SugarContext } from '../../../../../../mfe/src/internal-messaging';
import { FilterOperators } from '../../../discovery/filter/FilterOperators';
import { FilterTypes } from '../../../discovery/filter/Filter';
import EqualsFilter from '../../../discovery/filter/exports/EqualsFilter';

export const useDiscoveryIdSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const id = useSelector(state =>
    VIZ_SELECTORS.getDiscoveryId(state, { discoveryId }),
  );
  return id;
};

export const useOpenDiscoveriesSelector = () => {
  const openDiscoveries: { [key: string]: IDiscoveryTracked } = useSelector(
    (state: any) => state.discover?.openDiscoveries,
  );
  return openDiscoveries;
};

export const useConfigPanelDetailSelector = ({ discoveryId }) => {
  const configPanelDetail = useSelector(state =>
    getConfigPanelDetail(state, { discoveryId }),
  );
  return configPanelDetail;
};

export const useOpenDiscoverySelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const _discoveryId = useDiscoveryIdSelector({ discoveryId });
  const openDiscoveries = useOpenDiscoveriesSelector();
  const openDiscovery = useMemo(() => {
    return openDiscoveries[_discoveryId];
  }, [_discoveryId, openDiscoveries]);
  return openDiscovery;
};

export const useOpenDiscoveryPresentStateSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const open = useOpenDiscoverySelector({ discoveryId });
  const present = useMemo(() => open?.present, [open?.present]);
  return present;
};

export const useOpenVizSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const present = useOpenDiscoveryPresentStateSelector({ discoveryId });
  const viz = useMemo(() => present?.viz, [present?.viz]);
  return viz;
};

export const useOpenVizDatasetSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const viz = useOpenVizSelector({ discoveryId });
  return viz?.dataset;
};

export const useOpenVizLayoutSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}): ILayout => {
  const viz = useOpenVizSelector({ discoveryId });
  return viz?.layout;
};

export const useOpenVizChartSpec = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const viz = useOpenVizSelector({ discoveryId });
  return get(ChartSpecs, viz?.chartType);
};

export const useOpenVizDatasetAttributesSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const dataset = useOpenVizDatasetSelector({ discoveryId });
  return dataset?.attributes;
};

export const useActiveVizFieldSelector = ({
  discoveryId,
  name,
}: {
  discoveryId?: string;
  name: string;
}) => {
  const attributes = useOpenVizDatasetAttributesSelector({ discoveryId });
  const field = useMemo(() => _.find(attributes, { name }), [attributes, name]);
  return field;
};

export const useFieldAnnotationValueSelector = ({
  discoveryId,
  name,
  key,
}: {
  discoveryId?: string;
  name: string;
  key: string;
}) => {
  const field = useActiveVizFieldSelector({ discoveryId, name });
  const value = useMemo(
    () => _.find(field?.annotations, { key })?.value,
    [field?.annotations, key],
  );
  return value;
};

export const useVizOptionsSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const viz = useOpenVizSelector({ discoveryId });
  const options = useMemo(() => viz?.options, [viz?.options]);
  return options;
};

export const useVizCustomColorsSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const viz = useOpenVizSelector({ discoveryId });
  const options = useMemo(() => viz?.options, [viz?.options]);
  return parseJSON(options[VIZ_OPTION_IDS.customColors], {});
};

export const useWithSugarContextFilters = ({
  discoveryId,
}: { discoveryId?: string } = {}): IAppliedFilters => {
  const filters = useVizOptionSelector({
    discoveryId,
    option: VIZ_OPTION_IDS.filters,
  });

  const runtimeFilters = useRuntimeFilters(discoveryId);
  const availableRuntimeFilters = useMemo(
    () => values(runtimeFilters) ?? [],
    [runtimeFilters],
  );

  const {
    dashboardFilters: sugarRuntimeFilters,
    currentRecord: {
      allowCurrentRecord = false,
      showCurrentRecordFilter = false,
      module,
    },
  } = useContext(SugarContext);

  const dataset = useOpenVizDatasetSelector({ discoveryId });

  const additionalFilters = useMemo(() => {
    const baseFilters = convertSugarFilter(
      get(sugarRuntimeFilters, stripDashletSuffix(discoveryId), {}),
      availableRuntimeFilters,
    );

    if (allowCurrentRecord && showCurrentRecordFilter) {
      const recordReferences = filter(dataset?.attributes, _field =>
        some(_field?.annotations, { key: REF_TO, value: `${module}:id` }),
      );
      const field =
        find(recordReferences, _field => _field.available !== false) ??
        head(recordReferences);

      if (field) {
        const dynamicFieldOperator = _.get(DynamicFields, [module, 'id']);
        const dynamicModuleFilter = EqualsFilter(
          field as unknown as IAnyAttribute,
          [dynamicFieldOperator],
        );
        dynamicModuleFilter.expression.left.operator =
          FilterOperators.forFilterType(FilterTypes.STRING).dynamicField.key;

        return { ...baseFilters, [field.name]: dynamicModuleFilter };
      }
    }

    return baseFilters;
  }, [
    availableRuntimeFilters,
    discoveryId,
    sugarRuntimeFilters,
    allowCurrentRecord,
    showCurrentRecordFilter,
    dataset?.attributes,
    module,
  ]);

  return useMemo(() => {
    return !_.isEmpty(additionalFilters)
      ? { ...filters, ...additionalFilters }
      : filters;
  }, [additionalFilters, filters]);
};

const useParsedValue = ({ rawValue: raw, defaultValue, skipParse }) => {
  return useMemo(() => {
    if (_.isUndefined(raw)) {
      return defaultValue;
    }
    if (skipParse) {
      return raw;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }, [raw, skipParse, defaultValue]);
};

export const useVizOptionSelector = <T = any>({
  discoveryId,
  option,
  skipParse = false,
  defaultValue,
}: {
  discoveryId?: string;
  option: keyof IVizOptions;
  skipParse?: boolean;
  defaultValue?: T;
}) => {
  const options = useVizOptionsSelector({ discoveryId });
  const raw = _.get<IVizOptions, keyof IVizOptions>(options, option);
  return useParsedValue({
    rawValue: raw,
    defaultValue,
    skipParse,
  });
};

export const useReportLinkMaybeSideDrawer = ({
  discoveryId,
}): ILinkToReport => {
  const linkToReportOption = useVizOptionSelector({
    discoveryId,
    option: VIZ_OPTION_IDS.linkToReport,
    defaultValue: {},
  });

  const { isDashletMode = false } = useSelector(
    (state: any) => state.dashlet ?? {},
  );

  return useMemo(() => {
    if (isDashletMode) {
      linkToReportOption.id = addDashletSuffix(linkToReportOption?.id);
    }

    return linkToReportOption;
  }, [isDashletMode, linkToReportOption]);
};

export const useCustomFormatToggleSelector = ({
  discoveryId,
  toggleName,
  skipParse = false,
  defaultValue = {},
}) => {
  const viz = useOpenVizSelector({ discoveryId });
  const customFormatToggles = Viz.getCustomFormatTogglesFromViz(viz);

  const unparsedValue = _.find(customFormatToggles, { key: toggleName });

  return useParsedValue({
    rawValue: unparsedValue,
    defaultValue,
    skipParse,
  });
};

export const useRuntimeFilters = (discoveryId: string): FieldDictionary => {
  const viz = useOpenVizSelector({ discoveryId });

  const allFieldsDicionary = useMemo(() => {
    const allFields = Viz.getAllAvailableFields(viz);
    return Viz.createFieldDictionary('name', allFields);
  }, [viz]);

  const runtimeFilterDictionary = useVizOptionSelector<FieldDictionary>({
    discoveryId,
    option: VIZ_OPTION_IDS.runtimeFilters,
    defaultValue: (VIZ_SELECTORS.getOptionDefault(
      VIZ_OPTION_IDS.runtimeFilters,
    ) ?? {}) as FieldDictionary,
  });

  return mapValues(runtimeFilterDictionary, field =>
    get(allFieldsDicionary, field?.name, field),
  );
};

export const useHiddenFieldWarnings = ({
  discoveryId,
}: {
  discoveryId: string;
}): IHiddenFieldWarnings & { isBlocked: boolean } => {
  const viz = useOpenVizSelector({ discoveryId });
  const { chartType, dataset, layout, options } = viz ?? ({} as IViz);
  // Compared by value: the applied filters are a new object on every render while Sugar context filters exist.
  const appliedFiltersJson = JSON.stringify(
    useWithSugarContextFilters({ discoveryId }) ?? null,
  );

  return useMemo(() => {
    const warnings = Viz.findHiddenFieldWarnings(
      { chartType, dataset, layout, options } as IViz,
      JSON.parse(appliedFiltersJson) ?? undefined,
    );
    return {
      ...warnings,
      isBlocked:
        !_.isEmpty(warnings.hiddenFields) ||
        !_.isEmpty(warnings.hiddenFilters) ||
        !_.isEmpty(warnings.hiddenSettings),
    };
  }, [chartType, dataset, layout, options, appliedFiltersJson]);
};

export const useLiveQuerySelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const value = useVizOptionSelector<boolean>({
    discoveryId,
    option: VIZ_OPTION_IDS.useLiveQuery,
    defaultValue: VIZ_SELECTORS.getOptionDefault(VIZ_OPTION_IDS.useLiveQuery),
  });
  return value;
};

export const useVizNameSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const viz = useOpenVizSelector({ discoveryId });
  const vizName = useMemo(() => viz?.name, [viz?.name]);
  return vizName;
};

export const useUndoStackSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const open = useOpenDiscoverySelector({ discoveryId });
  const undoStack = useMemo(() => open?.past, [open?.past]);
  return undoStack;
};

export const useQueryErrorSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const present = useOpenDiscoveryPresentStateSelector({ discoveryId });
  const vizQueryError: IVizQueryError = useMemo(
    () => present?.vizQueryError,
    [present?.vizQueryError],
  );
  return vizQueryError;
};

export const useHasUndoItemsSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const undoStack = useUndoStackSelector({ discoveryId });
  return !_.isEmpty(undoStack);
};

export const useQueryErrorHttpCodeSelector = ({
  discoveryId,
}: { discoveryId?: string } = {}) => {
  const queryError: any = useQueryErrorSelector({ discoveryId });
  const statusCode: number = useMemo(
    () => queryError?.error?.networkError?.statusCode,
    [queryError?.error?.networkError?.statusCode],
  );
  return statusCode;
};
